import { ID_TOKEN_KEY } from '../constants';
import {
  ApplicationError,
  BusinessCheckError,
  ForbiddenError,
  NetworkError,
  ObjectNotFoundError,
  OptimisticLockError,
  SystemError,
  UnauthorizedError,
} from './ApplicationError';

function href(path: string) {
  return `${process.env.ORIGIN_SERVER_URL}${path}`;
}

function initRequest(): RequestInit {
  return {
    mode: 'cors',
    credentials: 'include',
    headers: new Headers({
      'Content-Type': 'application/json',
      Authorization: localStorage.getItem(ID_TOKEN_KEY) ?? '',
    }),
  };
}

async function handleInvalidRes(res: Response) {
  let payload;
  try {
    payload = await res.clone().json();
  } catch {
    payload = null;
  }

  switch (res.status) {
    case 400:
      return new BusinessCheckError(payload);
    case 401:
      return new UnauthorizedError(payload);
    case 403:
      return new ForbiddenError(payload);
    case 404:
      return new ObjectNotFoundError(payload);
    case 409:
      return new OptimisticLockError(payload);

    default:
      return new SystemError(payload);
  }
}

function shouldLogToPendo(init: RequestInit, res: Response) {
  if (!window.pendo) return false;
  if (!init.method || init.method.toUpperCase() !== 'POST') return false;
  return res.url.includes('admin/tenant');
}

function safeParseTenants(body: BodyInit | null | undefined): Array<Record<string, unknown>> {
  try {
    const parsed = JSON.parse(body as string) as {
      tenants?: Array<Record<string, unknown>>;
    };
    return parsed.tenants ?? [];
  } catch {
    return [];
  }
}

function pathOnly(rawUrl: string): string {
  try {
    return new URL(rawUrl).pathname;
  } catch {
    return rawUrl;
  }
}

async function extractErrorMessage(res: Response): Promise<string | null> {
  try {
    const errJson: unknown = await res.clone().json();
    const msg =
      typeof (errJson as { message?: unknown })?.message === 'string'
        ? (errJson as { message: string }).message
        : JSON.stringify(errJson);
    // 日本語メッセージは Pendo 側で 1 文字 6 bytes 相当に膨らむため 40 文字で打ち切る
    // (40 文字なら専用 error event が最大 411 bytes で 512 bytes 制限に収まる)。
    return msg.slice(0, 40);
  } catch {
    return null;
  }
}

/** tenant 情報の event を送信し、バッチの件数 (単一 tenant 時は 0) を返す。 */
function trackTenantEvents(
  commonPayload: Record<string, unknown>,
  init: RequestInit,
  isBatch: boolean,
): number {
  if (!isBatch) {
    // 単一 tenant: 従来通り form: init.body を送信
    window.pendo?.track('onboarding_front', { ...commonPayload, form: init.body });
    return 0;
  }

  // バッチは tenant 単位に分割して pendo.track を N 回呼ぶ。
  // Pendo の properties JSON 512 bytes 制限を回避するため、1 event に全 tenant を
  // 詰めず、1 event = 1 tenant として送信する。
  const tenants = safeParseTenants(init.body);
  tenants.forEach((t, i) => {
    window.pendo?.track('onboarding_front', {
      ...commonPayload,
      type: 'batch',
      batchTotal: tenants.length,
      batchIndex: i,
      tenant: String(t.tenant ?? ''),
      tenantAlias: String(t.alias ?? ''),
      version: String(t.version ?? ''),
      plan: String(t.plan ?? ''),
      silo: String(t.silo ?? ''),
      modelGroup: t.modelGroup ? '1' : '0',
    });
  });
  return tenants.length;
}

/**
 * エラー情報を専用 event として送信する。tenant 情報と同じ event に載せると
 * Pendo の properties JSON 512 bytes 制限を超えるため、1 リクエストにつき 1 回だけ送る。
 */
async function trackErrorEvent(
  commonPayload: Record<string, unknown>,
  res: Response,
  isBatch: boolean,
  batchTotal: number,
) {
  const errorMessage = await extractErrorMessage(res);
  if (errorMessage === null) return;

  const payload: Record<string, unknown> = {
    ...commonPayload,
    type: isBatch ? 'batchError' : 'error',
    errorMessage,
  };
  if (isBatch) payload.batchTotal = batchTotal;
  window.pendo?.track('onboarding_front', payload);
}

async function handleApiLogToPendo(_path: string, init: RequestInit, res: Response) {
  if (!shouldLogToPendo(init, res)) return;

  const isBatch = res.url.includes('/admin/tenants/batch');
  const commonPayload: Record<string, unknown> = {
    event: 'submitForm',
    timestamp: Date.now(),
    // 512 bytes 制限対策でパスのみ送信する。環境判定はレビュー側で Pendo の
    // server フィールドを使うため、ホストを含めなくても支障はない。
    url: pathOnly(res.url),
    responseStatus: res.status,
    responseOk: res.ok,
  };

  const batchTotal = trackTenantEvents(commonPayload, init, isBatch);
  if (res.ok) return;
  await trackErrorEvent(commonPayload, res, isBatch, batchTotal);
}

async function safeFetch<T>(path: string, init: RequestInit) {
  try {
    const res = await fetch(path, init);
    await handleApiLogToPendo(path, init, res);
    if (res.ok) {
      return (await res.json()) as T;
    } else {
      throw await handleInvalidRes(res);
    }
  } catch (e) {
    if (!(e instanceof ApplicationError)) throw new NetworkError();
    throw e;
  }
}

function serializeToSearchParams(params: Record<string, number | string>) {
  return Object.keys(params).length === 0
    ? ''
    : `?${new URLSearchParams(
        Object.fromEntries(Object.entries(params).map(([k, v]) => [k, v.toString()])),
      ).toString()}`;
}

async function get<T>(path: string, params: Record<string, number | string> = {}) {
  return safeFetch<T>(`${href(path)}${serializeToSearchParams(params)}`, {
    ...initRequest(),
    method: 'GET',
  });
}

async function post<T>(path: string, body: object = {}) {
  return safeFetch<T>(href(path), {
    ...initRequest(),
    method: 'POST',
    body: JSON.stringify(body),
  });
}

async function put<T>(path: string, body: object = {}) {
  return safeFetch<T>(href(path), {
    ...initRequest(),
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

async function del<T>(path: string, body: object = {}) {
  return safeFetch<T>(href(path), {
    ...initRequest(),
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

export { del, get, post, put, serializeToSearchParams };
